import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Edit, Eye, EyeOff, Save, Trash2, Upload, Image as ImageIcon } from "lucide-react";
import { MultiImageUpload } from "@/components/MultiImageUpload";
import { ImageGallery } from "@/components/ImageGallery";

interface ChateauEditorWithUploadProps {
  chateau: any;
  chateauId: string;
  onSave: (updatedChateau: any) => void;
  onDelete: () => void;
  onToggleVisibility: () => void;
  isHidden: boolean;
}

export const ChateauEditorWithUpload = ({
  chateau,
  chateauId,
  onSave,
  onDelete,
  onToggleVisibility,
  isHidden
}: ChateauEditorWithUploadProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedChateau, setEditedChateau] = useState(chateau);

  const handleSave = () => {
    // S'assurer que caracteristiques est un tableau avant la sauvegarde
    const chateauToSave = {
      ...editedChateau,
      caracteristiques: Array.isArray(editedChateau.caracteristiques) 
        ? editedChateau.caracteristiques 
        : (typeof editedChateau.caracteristiques === 'string' ? editedChateau.caracteristiques.split(',').map((item: string) => item.trim()).filter((item: string) => item.length > 0) : [])
    };
    onSave(chateauToSave);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedChateau(chateau);
    setIsEditing(false);
  };

  const updateField = (field: string, value: any) => {
    setEditedChateau((prev: any) => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <Card className="mb-8 border-2 border-primary/20 bg-primary/5">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center text-primary">
            <Edit className="w-5 h-5 mr-2" />
            Mode Administrateur - {chateau.nom}
          </CardTitle>
          <div className="flex gap-2">
            <Button
              variant={isHidden ? "outline" : "default"}
              size="sm"
              onClick={onToggleVisibility}
            >
              {isHidden ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
              {isHidden ? "Caché" : "Visible"}
            </Button>
            <Button variant="destructive" size="sm" onClick={onDelete}>
              <Trash2 className="w-4 h-4 mr-2" />
              Supprimer
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        <Tabs defaultValue="details" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="details">
              <Edit className="w-4 h-4 mr-2" />
              Détails
            </TabsTrigger>
            <TabsTrigger value="images">
              <ImageIcon className="w-4 h-4 mr-2" />
              Images
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="details" className="space-y-4">
            {isEditing ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Nom du château</Label>
                  <Input
                    value={editedChateau.nom}
                    onChange={(e) => updateField('nom', e.target.value)}
                  />
                </div>
                <div>
                  <Label>Prix</Label>
                  <Input
                    value={editedChateau.prix}
                    onChange={(e) => updateField('prix', e.target.value)}
                  />
                </div>
                <div>
                  <Label>Taille</Label>
                  <Input
                    value={editedChateau.taille}
                    onChange={(e) => updateField('taille', e.target.value)}
                  />
                </div>
                <div>
                  <Label>Capacité</Label>
                  <Input
                    value={editedChateau.capacite}
                    onChange={(e) => updateField('capacite', e.target.value)}
                  />
                </div>
                <div>
                  <Label>Âge recommandé</Label>
                  <Input
                    value={editedChateau.ageRecommande}
                    onChange={(e) => updateField('ageRecommande', e.target.value)}
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>Description</Label>
                  <Textarea
                    value={editedChateau.description}
                    onChange={(e) => updateField('description', e.target.value)}
                    rows={3}
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>Caractéristiques (séparées par des virgules)</Label>
                  <Input
                    value={
                      Array.isArray(editedChateau.caracteristiques) 
                        ? editedChateau.caracteristiques.join(', ') 
                        : editedChateau.caracteristiques || ''
                    }
                    onChange={(e) => {
                      // Stocker comme string pendant l'édition
                      updateField('caracteristiques', e.target.value);
                    }}
                    onBlur={(e) => {
                      // Convertir en tableau lors de la perte de focus
                      const value = e.target.value;
                      if (typeof value === 'string') {
                        const caracteristiques = value.split(',').map((item: string) => item.trim()).filter((item: string) => item.length > 0);
                        updateField('caracteristiques', caracteristiques);
                      }
                    }}
                    placeholder="Toboggan intégré, Thème dinosaure, Zone sécurisée"
                  />
                </div>
                <div className="md:col-span-2 flex gap-2">
                  <Button onClick={handleSave}>
                    <Save className="w-4 h-4 mr-2" />
                    Sauvegarder
                  </Button>
                  <Button variant="outline" onClick={handleCancel}>
                    Annuler
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <span className="text-sm font-medium text-muted-foreground">Prix</span>
                    <p className="font-semibold">{chateau.prix}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-muted-foreground">Taille</span>
                    <p className="font-semibold">{chateau.taille}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-muted-foreground">Capacité</span>
                    <p className="font-semibold">{chateau.capacite}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-muted-foreground">Âge</span>
                    <p className="font-semibold">{chateau.ageRecommande}</p>
                  </div>
                </div>
                
                <div>
                  <span className="text-sm font-medium text-muted-foreground">Caractéristiques</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(Array.isArray(chateau.caracteristiques) ? chateau.caracteristiques : []).map((carac: string, index: number) => (
                      <Badge key={index} variant="secondary" className="text-xs">
                        {carac}
                      </Badge>
                    ))}
                  </div>
                </div>
                
                <Button onClick={() => setIsEditing(true)}>
                  <Edit className="w-4 h-4 mr-2" />
                  Modifier les détails
                </Button>
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="images" className="space-y-6">
            <div>
              <h4 className="font-semibold mb-4 flex items-center">
                <Upload className="w-4 h-4 mr-2" />
                Upload Multiple d'Images
              </h4>
              <MultiImageUpload 
                chateauId={chateauId}
                onImagesUploaded={(imageUrls) => {
                  console.log('Images uploadées:', imageUrls);
                  // Rafraîchir la galerie
                }}
                maxFiles={10}
              />
            </div>
            
            <div>
              <h4 className="font-semibold mb-4 flex items-center">
                <ImageIcon className="w-4 h-4 mr-2" />
                Galerie d'Images
              </h4>
              <ImageGallery 
                chateauId={chateauId}
                onImageDeleted={() => {
                  console.log('Image supprimée');
                }}
              />
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};